import { useEffect, useMemo, useState } from "react";
import { arrayHasItems, formatCurrencyUsdFixedTwo } from "../../../helpers/common";
import Drawer from "../../Drawer";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

const toForecastDemandProductId = (variant) =>
    Number.parseInt(String(variant?.id ?? ""), 10);

/** Line-level replenishment cash: prefer API map by forecast row id, else split group cash by units. */
const getVariantLineCash = (variant, totalCash, allVariants, cashById) => {
    const id = toForecastDemandProductId(variant);
    if (
        Number.isFinite(id) &&
        id > 0 &&
        cashById &&
        Object.prototype.hasOwnProperty.call(cashById, id)
    ) {
        return Number(cashById[id]) || 0;
    }
    const totalUnits =
        allVariants.reduce((s, v) => s + (Number(v.units) || 0), 0) || 1;
    return totalCash * ((Number(variant.units) || 0) / totalUnits);
};

const skuUnitsSummary = (skuCount, units) =>
    `${skuCount || 0} ${skuCount === 1 ? "SKU" : "SKUs"} · ${units || 0} ${units === 1 ? "unit" : "units"}`;

export const SplitExceptionsDrawer = ({
    open,
    onClose,
    draftedPo,
    replenishmentCashByForecastProductId = {},
    onPersistSplit,
    marginFromSidebar,
    isPersisting = false,
}) => {
    const draftedPOname = draftedPo?.groupName ?? "N/A";
    const supplierLine = String(draftedPo?.supplierName ?? "").trim();
    const totalCash = Number(draftedPo?.cashRequired) || 0;
    const variants = arrayHasItems(draftedPo?.variants) ? draftedPo.variants : [];

    const [poBIds, setPoBIds] = useState([]);

    useEffect(() => {
        if (!open || !draftedPo) return;
        const variantList = arrayHasItems(draftedPo.variants) ? draftedPo.variants : [];
        const variantIdSet = new Set(
            variantList
                .map((v) => toForecastDemandProductId(v))
                .filter((n) => Number.isFinite(n) && n > 0)
        );
        const raw = draftedPo.exceptionForecastDemandProductIds;
        const next = Array.isArray(raw)
            ? raw
                .map((x) => Number.parseInt(String(x ?? ""), 10))
                .filter((n) => Number.isFinite(n) && variantIdSet.has(n))
            : [];
        setPoBIds(next);
    }, [open, draftedPo?.id]);

    const poBSet = useMemo(() => new Set(poBIds), [poBIds]);

    const poAVariants = useMemo(
        () => variants.filter((v) => !poBSet.has(toForecastDemandProductId(v))),
        [variants, poBSet]
    );
    const poBVariants = useMemo(
        () => variants.filter((v) => poBSet.has(toForecastDemandProductId(v))),
        [variants, poBSet]
    );

    const lineCash = (v) =>
        getVariantLineCash(v, totalCash, variants, replenishmentCashByForecastProductId);

    const sumCash = (list) => list.reduce((acc, v) => acc + lineCash(v), 0);
    const sumUnits = (list) => list.reduce((acc, v) => acc + (Number(v.units) || 0), 0);

    const cashRequiredForTheAPO = sumCash(poAVariants);
    const cashRequiredForTheBPO = sumCash(poBVariants);

    const carveOut = (variant) => {
        const id = toForecastDemandProductId(variant);
        if (!Number.isFinite(id) || id <= 0) return;
        setPoBIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    };

    const returnToA = (variant) => {
        const id = toForecastDemandProductId(variant);
        if (!Number.isFinite(id)) return;
        setPoBIds((prev) => prev.filter((x) => x !== id));
    };

    const resetSplit = () => setPoBIds([]);

    const handleDone = async () => {
        if (!onPersistSplit) return;
        try {
            await onPersistSplit(poBIds);
            onClose();
        } catch (error){
            console.error(error);
        }
    };

    const renderSkuRow = (variant, mode) => {
        const id = toForecastDemandProductId(variant);
        const units = Number(variant.units) || 0;
        const cash = lineCash(variant);
        const notes =
            variant.notes != null && String(variant.notes).trim() !== ""
                ? String(variant.notes).trim()
                : "No planner notes";

        return (
            <div
                key={id || variant.sku}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/70 bg-white px-3 py-2 text-sm"
                draggable
            >
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-primary">{variant.sku || ""}</p>
                        {mode === "b" && (
                            <span className="inline-flex items-center justify-center rounded-md border border-transparent bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-700 shadow-none">
                                Carved out
                            </span>
                        )}
                    </div>
                    <p className="truncate text-xs text-gray-600">{notes}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center justify-center w-fit whitespace-nowrap rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-primary shadow-none">
                        {units} {units === 1 ? "unit" : "units"}
                    </span>
                    <span className="inline-flex items-center justify-center w-fit whitespace-nowrap rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-primary shadow-none">
                        {formatCurrencyUsdFixedTwo(cash)}
                    </span>
                    {mode === "a" ? (
                        <button
                            type="button"
                            onClick={() => carveOut(variant)}
                            disabled={isPersisting}
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border bg-transparent shadow-xs hover:bg-gray-50 dark:bg-transparent dark:border-input dark:hover:bg-input/50 py-2 has-[>svg]:px-3 h-8 rounded-md px-3"
                        >
                            Carve out to PO B
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => returnToA(variant)}
                            disabled={isPersisting}
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border bg-transparent shadow-xs hover:bg-gray-50 dark:bg-transparent dark:border-input dark:hover:bg-input/50 py-2 has-[>svg]:px-3 h-8 rounded-md px-3"
                        >
                            Return to PO A
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const poAUnits = sumUnits(poAVariants);
    const poBUnits = sumUnits(poBVariants);

    return (
        <Drawer
            isOpen={open}
            onClose={onClose}
            width="1100px"
            marginFromSidebar={marginFromSidebar}
            headerContent={
                <div className="flex flex-col gap-1.5 py-2 pb-0 text-left">
                    <h2 className="text-primary font-semibold">{`${draftedPOname} · Draft PO split`}</h2>
                    <p className="text-gray-600 text-sm">
                        <span>Keep the drafted product together on </span>
                        <span className="font-medium text-primary"> PO A </span>
                        <span>
                            by default. Only carve out the manufacturing or material exceptions that truly need their
                            own{" "}
                        </span>
                        <span className="font-medium text-primary"> PO B </span>
                        <span>, such as KLW-MGMTL.</span>
                    </p>
                </div>
            }
            footer={
                <div className="mt-auto flex flex-col gap-2 border-t border-border p-4 px-5 py-4 sm:flex-row sm:justify-between">
                    <p className="text-sm text-gray-600">
                        This workspace stages exceptions inside a single drafted PO. Review the carve-out before moving
                        on to the next product.
                    </p>
                    <button
                        type="button"
                        onClick={handleDone}
                        disabled={isPersisting || !draftedPo}
                        className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white outline-none transition-all hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 [&_svg]:has-[>svg]:px-3"
                    >
                        Done reviewing split
                    </button>
                </div>
            }
        >
            <div className="flex h-full flex-col overflow-hidden">
                <div className="border-b border-border px-5 py-4">
                    <div className="grid gap-3 lg:grid-cols-[repeat(4,minmax(0,1fr))]">
                        <div className="rounded-md border border-border bg-white px-3 py-3">
                            <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">Drafted product</p>
                            <p className="mt-1 text-lg font-semibold text-primary">{draftedPOname}</p>
                            <p className="mt-1 text-xs text-gray-600">
                                {variants.length} {variants.length === 1 ? "SKU" : "SKUs"} ready for review
                            </p>
                        </div>
                        <div className="rounded-md border border-border bg-white px-3 py-3">
                            <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">Exception SKUs</p>
                            <p className="mt-1 text-lg font-semibold text-primary">{poBVariants.length}</p>
                            <p className="mt-1 text-xs text-gray-600">
                                Only manufacturing exceptions should move to PO B.
                            </p>
                        </div>
                        <div className="rounded-md border border-border bg-white px-3 py-3">
                            <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">PO A cash</p>
                            <p className="mt-1 text-lg font-semibold text-primary">
                                {formatCurrencyUsdFixedTwo(cashRequiredForTheAPO)}
                            </p>
                            <p className="mt-1 text-xs text-gray-600">{skuUnitsSummary(poAVariants.length, poAUnits)}</p>
                        </div>
                        <div className="rounded-md border border-border bg-white px-3 py-3">
                            <p className="text-[11px] uppercase tracking-[0.12em] text-gray-600">PO B cash</p>
                            <p className="mt-1 text-lg font-semibold text-primary">
                                {formatCurrencyUsdFixedTwo(cashRequiredForTheBPO)}
                            </p>
                            <p className="mt-1 text-xs text-gray-600">{skuUnitsSummary(poBVariants.length, poBUnits)}</p>
                        </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={resetSplit}
                            disabled={isPersisting || poBIds.length === 0}
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border bg-transparent shadow-xs hover:bg-gray-50 dark:bg-transparent dark:border-input dark:hover:bg-input/50 py-1 has-[>svg]:px-3 h-auto rounded-md px-3"
                        >
                            <ArrowPathIcon className="h-4 w-4" />
                            <span>Reset exception split</span>
                        </button>
                        <span
                            className="inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md border border-border bg-white px-2 py-1.5 text-[11px] font-medium text-primary shadow-none"
                        >
                            Products stay together by default. Move only the true exception SKUs to PO B.
                        </span>
                        {arrayHasItems(poBVariants) && (
                            <span
                                className="inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md border border-border bg-white px-2 py-1.5 text-[11px] font-medium text-primary shadow-none"
                            >
                                {poBVariants.length} drafted product currently have exception carve-outs
                            </span>
                        )}
                    </div>
                </div>
                <div className="grid min-h-0 flex-1 gap-4 overflow-hidden px-5 py-4 xl:grid-cols-2">
                    {/* PO A */}
                    <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-white">
                        <div className="border-b border-border px-4 py-4">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600">PO A</p>
                                    <h3 className="mt-1 text-base font-semibold text-primary">Primary drafted PO</h3>
                                </div>
                                <div className="text-right text-xs text-gray-600">
                                    <p>{skuUnitsSummary(poAVariants.length, poAUnits)}</p>
                                    <p className="mt-1 font-medium text-primary">
                                        {formatCurrencyUsdFixedTwo(cashRequiredForTheAPO)}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="min-h-0 flex-1 space-y-3 overflow-auto px-4 py-4">
                            {arrayHasItems(poAVariants) ? (
                                <div className="rounded-md border border-border bg-background p-3">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="text-sm font-semibold text-primary">{draftedPOname}</p>
                                                <span className="inline-flex items-center justify-center rounded-md border border-transparent bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-700 shadow-none">
                                                    Home PO
                                                </span>
                                            </div>
                                            <p className="mt-1 text-xs text-gray-600">{supplierLine}</p>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="inline-flex items-center justify-center w-fit whitespace-nowrap rounded-md border border-border bg-white px-2 py-1 text-[11px] font-medium text-primary shadow-none">
                                                {poAVariants.length} {poAVariants.length === 1 ? "SKU" : "SKUs"}
                                            </span>
                                            <span className="inline-flex items-center justify-center w-fit whitespace-nowrap rounded-md border border-border bg-white px-2 py-1 text-[11px] font-medium text-primary shadow-none">
                                                {formatCurrencyUsdFixedTwo(cashRequiredForTheAPO)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-3 space-y-2">{poAVariants.map((v) => renderSkuRow(v, "a"))}</div>
                                </div>
                            ) : (
                                <div className="rounded-md border border-dashed border-border bg-background px-4 py-8 text-sm text-gray-600 whitespace-break-spaces">
                                    All SKUs have been carved out of the drafted PO. Return any SKU here if it should stay with the main product order.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* PO B */}
                    <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-white">
                        <div className="border-b border-border px-4 py-4">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600">PO B</p>
                                    <h3 className="mt-1 text-base font-semibold text-primary">
                                        Exception carve-out PO
                                    </h3>
                                </div>
                                <div className="text-right text-xs text-gray-600">
                                    <p>{skuUnitsSummary(poBVariants.length, poBUnits)}</p>
                                    <p className="mt-1 font-medium text-primary">
                                        {formatCurrencyUsdFixedTwo(cashRequiredForTheBPO)}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="min-h-0 flex-1 space-y-3 overflow-auto px-4 py-4">
                            {arrayHasItems(poBVariants) ? (
                                <div className="rounded-md border border-border bg-background p-3">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="text-sm font-semibold text-primary">{draftedPOname}</p>
                                                <span className="inline-flex items-center justify-center rounded-md border border-transparent bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-700 shadow-none">
                                                    Exception SKU carve-out
                                                </span>
                                            </div>
                                            <p className="mt-1 text-xs text-gray-600">{supplierLine}</p>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="inline-flex items-center justify-center w-fit whitespace-nowrap rounded-md border border-border bg-white px-2 py-1 text-[11px] font-medium text-primary shadow-none">
                                                {poBVariants.length} {poBVariants.length === 1 ? "SKU" : "SKUs"}
                                            </span>
                                            <span className="inline-flex items-center justify-center w-fit whitespace-nowrap rounded-md border border-border bg-white px-2 py-1 text-[11px] font-medium text-primary shadow-none">
                                                {formatCurrencyUsdFixedTwo(cashRequiredForTheBPO)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-3 space-y-2">
                                        {poBVariants.length === 0 ? (
                                            <p className="rounded-md border border-dashed border-border/70 px-3 py-6 text-center text-sm text-gray-600">
                                                No exception SKUs yet. Use &quot;Carve out to PO B&quot; on PO A.
                                            </p>
                                        ) : (
                                            poBVariants.map((v) => renderSkuRow(v, "b"))
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-md border border-dashed border-border bg-background px-4 py-8 text-sm text-gray-600 whitespace-break-spaces">
                                    No exception SKUs have been carved out yet. Move only the manufacturing exceptions here.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};
